import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import KeyIcon from '@material-ui/icons/VpnKey';
import EditIcon from '@material-ui/icons/Edit';
import LocationOn from '@material-ui/icons/LocationOn';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { useEffectSkipFirst } from 'hooks/common';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    boxShadow: 'none',
    position: 'unset',
  },
  summaryRoot: {
    paddingLeft: 0,
    paddingRight: 0,
    display: 'flex',
    justifyContent: 'flex-start',
  },
  summaryContent: {
    alignItems: 'center',
    flexGrow: 'unset',
  },
  heading: {
    fontSize: theme.text.subheader.fontSize,
    fontWeight: theme.text.header.fontWeight,
    color: theme.palette.gray.gray_4,
  },
  checkboxLabel: {
    fontSize: theme.text.content.fontSize,
    fontWeight: theme.text.content.fontWeight,
    color: theme.palette.gray.gray_4,
  },
  checkbox: {
    width: 28,
    height: 28,
    borderRadius: 7,
    marginRight: 21,
  },
  detailCheckbox: {
    width: 28,
    height: 28,
    borderRadius: 7,
    marginRight: 10,
  },
  detailContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginRight: 24,
  },
  draggingItem: {
    opacity: 0.6,
  },
  dragHandle: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 12,
    color: theme.palette.gray.gray_5,
    cursor: 'grab',
  },
  dragHandleActive: {
    cursor: 'grabbing',
    color: theme.palette.gray.gray_6,
  },
}));

export function MyCheckbox(props) {
  const { value, setValue, header, labelStyle, checkboxStyle, containerStyle } =
    props;
  const classes = useStyles();
  const isChecked = Boolean(value);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...containerStyle,
      }}
    >
      <Checkbox
        onChange={e => {
          setValue(e.target.checked);
        }}
        className={classes.checkbox}
        color="primary"
        icon={<CheckBoxOutlineBlankIcon style={{ fontSize: 28 }} />}
        checkedIcon={<CheckBoxIcon style={{ fontSize: 28 }} />}
        checked={isChecked}
        value={isChecked}
        style={checkboxStyle}
      />
      <Typography className={classes.heading} style={labelStyle}>
        {header}
      </Typography>
    </div>
  );
}
MyCheckbox.defaultProps = {
  labelStyle: {},
  checkboxStyle: {},
  containerStyle: {},
};

export default function CheckboxList(props) {
  const classes = useStyles();
  const {
    data,
    setData,
    getName,
    containerStyle,
    labelStyle,
    showCheckAll,
    refreshCheckAll,
    enableDragDrop = false,
  } = props;


  const [checkAll, setCheckAll] = useState(Boolean(props.checkAll));
  const [draggingIndex, setDraggingIndex] = useState(null);
  const draggingItemIndexRef = useRef(null);

  const handleCheckboxOnChange = (e, item) => {
    item.checked = !!e.target.checked;
    const newData = data.map(el => {
      // gambiarra no _id pra deixar atualizar sem dar erro
      return {
        ...el,
        checked: (item._id === el._id && item.checked) || el.checked,
      };
    });
    setData(newData);
  };

  useEffectSkipFirst(() => {
    setCheckAll(false);
  }, [refreshCheckAll]);

  useEffectSkipFirst(() => {
    const newData = data.map(e => {
      // gambiarra no _id pra deixar atualizar sem dar erro
      return { ...e, checked: checkAll };
    });
    setData(newData);
  }, [checkAll]);

  const getValue = item => {
    return !!item.checked;
  };

  useEffect(() => {
    if (!enableDragDrop) {
      handleDragEnd();
    }
  }, [enableDragDrop]);

  const reorderItems = (startIndex, targetIndex, shouldInsertAfter = false) => {
    if (
      startIndex === null ||
      targetIndex === null ||
      data.length === 0 ||
      startIndex < 0 ||
      startIndex >= data.length
    ) {
      return;
    }

    let normalizedTarget = shouldInsertAfter ? targetIndex + 1 : targetIndex;

    if (normalizedTarget < 0) {
      normalizedTarget = 0;
    }

    if (normalizedTarget > data.length) {
      normalizedTarget = data.length;
    }

    const updated = [...data];
    const [movedItem] = updated.splice(startIndex, 1);

    if (!movedItem) {
      return;
    }

    if (startIndex < normalizedTarget) {
      normalizedTarget -= 1;
    }

    if (normalizedTarget < 0) {
      normalizedTarget = 0;
    }

    if (normalizedTarget > updated.length) {
      normalizedTarget = updated.length;
    }

    updated.splice(normalizedTarget, 0, movedItem);
    setData(updated);
  };

  const handleDragStart = (event, index) => {
    draggingItemIndexRef.current = index;
    setDraggingIndex(index);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  };

  const handleDragOver = event => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDropOnItem = (event, index) => {
    event.preventDefault();
    event.stopPropagation();
    const fromIndex = draggingItemIndexRef.current;
    if (fromIndex === null) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const offsetY = event.clientY - rect.top;
    const shouldInsertAfter = offsetY > rect.height / 2;
    reorderItems(fromIndex, index, shouldInsertAfter);
    handleDragEnd();
  };

  const handleListDrop = event => {
    event.preventDefault();
    const fromIndex = draggingItemIndexRef.current;
    if (fromIndex === null) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const shouldInsertAtEnd = event.clientY > rect.top + rect.height / 2;
    if (shouldInsertAtEnd) {
      reorderItems(fromIndex, data.length, false);
    } else {
      reorderItems(fromIndex, 0, false);
    }
    handleDragEnd();
  };

  const handleDragEnd = () => {
    draggingItemIndexRef.current = null;
    setDraggingIndex(null);
  };


  useEffect(() => {
    if (!enableDragDrop) {
      handleDragEnd();
    }
  }, [enableDragDrop]);

  return (
    <div
      className={classes.root}
      style={{ ...containerStyle }}
      onDragOver={enableDragDrop ? handleDragOver : undefined}
      onDrop={enableDragDrop ? handleListDrop : undefined}
    >
      {showCheckAll && (
        <MyCheckbox
          value={checkAll}
          setValue={setCheckAll}
          header="Todos"
          containerStyle={{ marginRight: 24 }}
        ></MyCheckbox>
      )}
      {data.map((item, index) => {
        const detailClasses = [classes.detailContainer];
        if (draggingIndex === index) {
          detailClasses.push(classes.draggingItem);
        }

        return (
          <div
            className={detailClasses.join(' ')}
            key={item._id || index}
            onDragOver={enableDragDrop ? handleDragOver : undefined}
            onDrop={
              enableDragDrop
                ? event => handleDropOnItem(event, index)
                : undefined
            }
          >
            {enableDragDrop ? (
              <div
                className={`${classes.dragHandle} ${
                  draggingIndex === index ? classes.dragHandleActive : ''
                }`}
                draggable
                onDragStart={event => handleDragStart(event, index)}
                onDragOver={handleDragOver}
                onDrop={event => handleDropOnItem(event, index)}
                onDragEnd={handleDragEnd}
              >
                <DragIndicatorIcon fontSize="small" />
              </div>
            ) : null}
            <Checkbox
              onChange={e => {
                handleCheckboxOnChange(e, item);
              }}
              className={classes.checkbox}
              color="primary"
              icon={<CheckBoxOutlineBlankIcon style={{ fontSize: 28 }} />}
              checkedIcon={<CheckBoxIcon style={{ fontSize: 28 }} />}
              checked={getValue(item)}
              value={getValue(item)}
            />
            <Typography className={classes.heading} style={{ ...labelStyle }}>
              {getName(item)}{' '}
              {props.isUserForm && getValue(item) ? (
                <EditIcon
                  style={{ fontSize: 18, cursor: 'pointer' }}
                  onClick={props.onClickEditIcon(getName(item), item._id)}
                />
              ) : null}{' '}
              {/* {props.isUserForm && getValue(item) ? (
                <LocationOn
                  style={{ fontSize: 18, cursor: 'pointer' }}
                  onClick={props.onClickMapIcon(getName(item), item._id)}
                />
              ) : null}{' '} */}
              {props.isUserForm &&
              props.iconsLabelList.includes(getName(item)) &&
              getValue(item) ? (
                <KeyIcon
                  style={{ fontSize: 18, cursor: 'pointer' }}
                  onClick={props.onClickKeyIcon}
                />
              ) : null}
            </Typography>
          </div>
        );
      })}
    </div>
  );
}

CheckboxList.defaultProps = {
  containerStyle: {},
  labelStyle: {},
  checkAll: false,
  showCheckAll: false,
  refreshCheckAll: false,
  enableDragDrop: false,
};
